import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToast } from '@/components/ui';
import { createProjectEditRequest } from '@/api/requests';
import { adminAddPlatform, adminUpdateProject } from '@/api/projects';
import type { AdminUpdateProjectBody } from '@/api/projects';
import type { Project } from '@/api/types';
import { getErrorMessage } from '@/api';
import { useMetaContext } from '@/components/Layout';

import CommonInfoCard from './CommonInfoCard';
import ProjectStageEditor from './ProjectStageEditor';
// 데이터 연동 정보 카드는 현재 표시할 항목이 없어 비활성화. tapjoy_app_id는 '플랫폼별 정보'로 이동.
// import DataLinkCard from './DataLinkCard';
import PlatformInfoCard from './PlatformInfoCard';
import ChangeSummaryCard from './ChangeSummaryCard';
import { ProcessStepper } from '@/components/ui';
import type { ProcessStepItem } from '@/components/ui';
import {
  buildCommon,
  buildPlatforms,
  emptyPlatformState,
  fieldsFor,
  isReadonlyField,
  platformLabel,
} from './infoTabFields';
import type { CommonState, DiffItem, PlatformState } from './infoTabFields';
import { editValueText } from './projectEditDiff';
import { useNetworkLookup } from './useNetworkLookup';

// 스텝퍼 단계 사이 시각적 텀 — 진행이 너무 빨라 단계가 안 보이는 것을 방지.
const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

interface InfoTabProps {
  project: Project;
  // admin 즉시 저장 / 비-admin 요청 성공 후 부모에서 project 데이터를 재fetch. project prop이
  // 새 값으로 들어오면 buildCommon/buildPlatforms 결과가 갱신되고 setVer로 form key가 바뀌어
  // 변경 내역(diff) 영역과 입력 form이 새 데이터로 초기화된다.
  onSaved?: () => void;
  // project.reload 진행 중 (data는 옛 값 유지) — platformBody에 로드 overlay 표시.
  reloading?: boolean;
}

export default function InfoTab({ project, onSaved, reloading = false }: InfoTabProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const meta = useMetaContext();
  const isAdmin = meta.current_user.platform_role === 'admin';

  const initialCommon = useMemo(() => buildCommon(project), [project]);
  const initialPlatforms = useMemo(() => buildPlatforms(project), [project]);
  // 관리자가 아니면 Stage 도 다른 칸처럼 '변경 사항'에 담아 변경 요청으로 보낸다.
  // 관리자는 ProjectStageEditor 에서 바로 저장하므로 이 값을 쓰지 않는다.
  const initialStage = project.stage ?? '';
  const platformCodes = useMemo(
    () => (project.platforms ?? []).map((p) => p.code),
    [project.platforms],
  );

  const [common, setCommon] = useState<CommonState>(initialCommon);
  const [platforms, setPlatforms] = useState<Record<string, PlatformState>>(initialPlatforms);
  const [stage, setStage] = useState(initialStage);
  const [savedStage, setSavedStage] = useState(initialStage);
  const [activeCode, setActiveCode] = useState<string>(platformCodes[0] ?? '');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ver, setVer] = useState(0);
  // 앱 정보 반영 과정 스텝퍼 — null이면 닫힘. admin 즉시 반영 시에만 사용.
  const [processSteps, setProcessSteps] = useState<ProcessStepItem[] | null>(null);

  // submit 성공 후 diffs 영역과 변경 사유 영역이 BQ reload 완료까지 남는 회귀를 막기 위한
  // saved snapshot. submit 직후 saved=current로 박아 diffs=0 즉시. parent가 project를 재fetch해
  // 새 initialCommon/initialPlatforms이 도착하면 useEffect로 saved도 새 값으로 sync.
  const [savedCommon, setSavedCommon] = useState<CommonState>(initialCommon);
  const [savedPlatforms, setSavedPlatforms] = useState<Record<string, PlatformState>>(initialPlatforms);

  // + 메뉴로 추가됐지만 아직 BQ에 INSERT되지 않은 임시 platform code 목록.
  // 저장 시 adminAddPlatform → adminUpdateProject 흐름으로 실제 BQ 반영.
  const [addingCodes, setAddingCodes] = useState<string[]>([]);
  // submit 성공 후 reload가 완료될 때까지(=새 initialPlatforms 도착할 때까지) overlay 유지.
  // useFetch의 loading 상태만 보면 reload 호출 직후 false→true 토글 사이에 frame이 빠질 수 있어
  // "다 된 것처럼 보이다가 잠시 후 틱 바뀌는" 회귀가 생긴다. 명시적 await flag로 일치.
  const [awaitingReload, setAwaitingReload] = useState(false);
  // 중복 제거 — submit 후 reload로 platformCodes에 새 code가 들어왔는데 addingCodes에도 남아 있을 때.
  const allPlatformCodes = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const c of [...platformCodes, ...addingCodes]) {
      if (!seen.has(c)) {
        seen.add(c);
        out.push(c);
      }
    }
    return out;
  }, [platformCodes, addingCodes]);

  // reload로 project.platforms에 추가된 code가 들어오면 addingCodes에서 제거 — "추가중" 라벨도 함께 사라짐.
  useEffect(() => {
    setAddingCodes((prev) => prev.filter((c) => !platformCodes.includes(c)));
  }, [platformCodes]);

  // 프로젝트 변경 시 폼/탭 재설정.
  useEffect(() => {
    setCommon(initialCommon);
    setPlatforms(initialPlatforms);
    setSavedCommon(initialCommon);
    setSavedPlatforms(initialPlatforms);
    setStage(initialStage);
    setSavedStage(initialStage);
    setReason('');
    setVer((v) => v + 1);
    if (platformCodes.length > 0 && !platformCodes.includes(activeCode)) {
      setActiveCode(platformCodes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.prefix]);

  // project가 reload돼서 새 데이터가 도착하면 saved를 새 initial로 sync + 대기 overlay 해제.
  // backend가 input 그대로 반영했다면 diffs는 0 그대로 유지된다.
  useEffect(() => {
    setSavedCommon(initialCommon);
    setSavedPlatforms(initialPlatforms);
    setSavedStage(initialStage);
    setAwaitingReload(false);
    // 스텝퍼 진행 중이었다면 '최신 정보 갱신' 단계 완료 처리 (전체 done → 확인 버튼 노출).
    setProcessSteps((prev) =>
      prev ? prev.map((s) => (s.key === 'reload' ? { ...s, status: 'done' } : s)) : prev,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCommon, initialPlatforms]);

  const setCommonField = <K extends keyof CommonState>(key: K, value: CommonState[K]) => {
    setCommon((prev) => ({ ...prev, [key]: value }));
  };

  const setPlatformField = (code: string, key: keyof PlatformState, value: string) => {
    setPlatforms((prev) => ({
      ...prev,
      [code]: { ...prev[code], [key]: value },
    }));
  };

  const { lookingUpNetwork, lookupNetwork, lookupAllNetworks } = useNetworkLookup(platforms, setPlatformField);

  const diffs = useMemo<DiffItem[]>(() => {
    const out: DiffItem[] = [];
    // diff 비교 기준은 saved snapshot — submit 성공 직후 saved=current로 박혀 즉시 0.
    // 빈 값('선택해 주세요')은 원장이 받지 않으므로 변경으로 담지 않는다.
    if (!isAdmin && stage && stage !== savedStage) {
      out.push({
        scope: '공통',
        field: 'stage',
        before: editValueText('stage', savedStage),
        after: editValueText('stage', stage),
      });
    }
    // 공통 필드 diff
    (Object.keys(savedCommon) as (keyof CommonState)[]).forEach((k) => {
      if (common[k] !== savedCommon[k]) {
        out.push({
          scope: '공통',
          field: String(k),
          before: savedCommon[k] || '(빈 값)',
          after: common[k] || '(빈 값)',
        });
      }
    });
    // platform별 필드 diff — sub_project_id는 readonly이므로 사용자가 바꿀 수 없음.
    // 추가 중(addingCodes)인 platform도 diff 비교 — saved에 빈 값으로 박혀 있어 입력값이 dirty로 잡힘.
    for (const code of allPlatformCodes) {
      const current = platforms[code];
      const saved = savedPlatforms[code];
      if (!current || !saved) continue;
      for (const key of fieldsFor(code)) {
        if (isReadonlyField(key)) continue;
        if (current[key] !== saved[key]) {
          out.push({
            scope: platformLabel(code),
            field: key,
            before: saved[key] || '(빈 값)',
            after: current[key] || '(빈 값)',
          });
        }
      }
    }
    return out;
  }, [common, platforms, savedCommon, savedPlatforms, allPlatformCodes, isAdmin, stage, savedStage]);

  const reset = () => {
    setCommon(initialCommon);
    setPlatforms(initialPlatforms);
    setStage(initialStage);
    setReason('');
    setVer((v) => v + 1);
    toast.info('변경 사항을 되돌렸어요');
  };

  const submit = async () => {
    // admin은 reason 미입력 허용 — request flow를 거치지 않고 즉시 UPDATE.
    if (!isAdmin && !reason.trim()) {
      toast.error('변경 사유를 입력해주세요');
      return;
    }
    if (diffs.length === 0) {
      toast.error('변경 사항이 없어요');
      return;
    }
    setSubmitting(true);
    try {
      // payload = { common: { changedFields... }, platforms: { code: { changedFields... } } }
      const commonChanges: Record<string, string> = {};
      const platformChanges: Record<string, Record<string, string>> = {};

      for (const d of diffs) {
        if (d.scope === '공통' && d.field === 'stage') {
          // 표시는 라벨(Stage 3)이지만 보낼 값은 원장 값(STAGE_3)이다.
          commonChanges.stage = stage;
        } else if (d.scope === '공통') {
          const k = d.field as keyof CommonState;
          commonChanges[d.field] = common[k];
        } else {
          // scope는 라벨 (AOS/iOS/...) — 원본 code로 매핑 (추가 중 코드도 포함).
          const code = allPlatformCodes.find((c) => platformLabel(c) === d.scope) ?? d.scope.toLowerCase();
          if (!platformChanges[code]) platformChanges[code] = {};
          platformChanges[code][d.field] = platforms[code][d.field as keyof PlatformState];
        }
      }

      if (isAdmin) {
        // admin: BIGQUERY_APPS_TABLE에 즉시 INSERT(추가 중 platform) + UPDATE(변경 필드). project_id 필수.
        const projectId = (project.project_id ?? '').trim();
        if (!projectId) {
          toast.error('project_id가 없는 프로젝트는 즉시 수정할 수 없어요');
          return;
        }
        // 반영 과정 스텝퍼 시작 — 1단계 utils.apps 반영 진행 중.
        setProcessSteps([
          { key: 'apps', label: 'utils.apps 반영', desc: 'BigQuery 앱 정보 업데이트', status: 'active' },
          {
            key: 'followups',
            label: 'BI 후속처리',
            desc: 'static.json · Redash · AWS UUID · Apple · PSL 동기화',
            status: 'pending',
          },
          { key: 'reload', label: '최신 정보 갱신', desc: '반영된 데이터 다시 불러오기', status: 'pending' },
        ]);

        // 1) addingCodes 먼저 INSERT 빈 row — 이후 UPDATE가 채움.
        for (const code of addingCodes) {
          await adminAddPlatform(projectId, code);
        }
        const adminBody: AdminUpdateProjectBody = {};
        if (Object.keys(commonChanges).length > 0) adminBody.common = commonChanges;
        if (Object.keys(platformChanges).length > 0) adminBody.platforms = platformChanges;
        // 2) common + platforms UPDATE.
        if (adminBody.common || adminBody.platforms) {
          await adminUpdateProject(projectId, adminBody);
        }
        // utils.apps 완료 → BI 후속처리 단계 진행 표시 (백엔드가 백그라운드 트리거).
        setProcessSteps((prev) =>
          prev
            ? prev.map((s) =>
                s.key === 'apps'
                  ? { ...s, status: 'done' }
                  : s.key === 'followups'
                    ? { ...s, status: 'active' }
                    : s,
              )
            : prev,
        );
        await delay(500);
        // BI 후속처리는 fire-and-forget(daemon thread) — 트리거 완료로 표시하고 갱신 단계로.
        setProcessSteps((prev) =>
          prev
            ? prev.map((s) =>
                s.key === 'followups'
                  ? {
                      ...s,
                      status: 'done',
                      desc: '백그라운드에서 진행돼요 (static.json · Redash · AWS UUID · Apple · PSL)',
                    }
                  : s.key === 'reload'
                    ? { ...s, status: 'active' }
                    : s,
              )
            : prev,
        );

        // saved snapshot을 즉시 user input으로 commit → diffs=0 → 변경 사유/내역 영역 즉시 사라짐.
        setSavedCommon(common);
        setSavedPlatforms(platforms);
        setAddingCodes([]);
        setReason('');
        // reload 대기 — useEffect [initialCommon, initialPlatforms]가 트리거되면 false로 자동 해제
        // 하면서 '최신 정보 갱신' 단계도 done 처리된다.
        setAwaitingReload(true);
        // 부모에서 project 데이터 재fetch (백그라운드). 새 initial 도착 시 useEffect로 saved sync.
        onSaved?.();
        setVer((v) => v + 1);
        return;
      }

      // 비-admin: 기존 request flow.
      // 데이터셋 접두어가 없거나 승인 전에 바뀌어도 같은 게임에 반영한다.
      const payload: Record<string, unknown> = { project_id: project.project_id };
      if (Object.keys(commonChanges).length > 0) payload.common = commonChanges;
      if (Object.keys(platformChanges).length > 0) payload.platforms = platformChanges;

      await createProjectEditRequest({
        prefix: project.prefix,
        payload,
        reason,
      });
      toast.success(`${diffs.length}개 필드 변경 요청을 보냈어요. 승인 후 반영됩니다`);
      // 비admin 요청은 승인 대기 상태 — 현재 상세에 머물 이유가 없으므로 프로젝트 리스트 초기화면으로.
      navigate('/');
      return;
    } catch (err) {
      // 실패 시 스텝퍼 닫고 에러 토스트 — 어느 단계든 중단 즉시 노출.
      setProcessSteps(null);
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // + 메뉴에서 OS 선택 — BQ 호출 없이 로컬 state에만 임시 platform 추가.
  const handlePickAddPlatform = (code: string) => {
    setAddingCodes((prev) => (prev.includes(code) ? prev : [...prev, code]));
    const empty = emptyPlatformState();
    setPlatforms((prev) => ({ ...prev, [code]: empty }));
    setSavedPlatforms((prev) => ({ ...prev, [code]: emptyPlatformState() }));
    setActiveCode(code);
    toast.info(`${platformLabel(code)} 추가 중 — 정보 입력 후 저장하면 반영돼요`);
  };

  return (
    <div>
      {/* 공통 정보 카드 */}
      <CommonInfoCard
        projectId={project.project_id ?? ''}
        common={common}
        ver={ver}
        onChangeField={setCommonField}
        stageEditor={project.metadata_source === 'public.apps' && (
          isAdmin
            ? <ProjectStageEditor project={project} canEdit={!submitting && !reloading} onSaved={onSaved} />
            : <ProjectStageEditor project={project} canEdit={!submitting && !reloading}
                requestValue={stage} onRequestChange={setStage} ver={ver} />
        )}
      />

      {/* 데이터 연동 정보 — 현재 표시할 항목 없어 비활성화(tapjoy_app_id는 플랫폼별 정보로 이동)
      <DataLinkCard common={common} ver={ver} onChangeField={setCommonField} /> */}

      {/* 플랫폼별 정보 — 탭으로 토글. 'web'/'steam' 등 새 platform code 자동 표시. */}
      {platformCodes.length > 0 && (
        <PlatformInfoCard
          allPlatformCodes={allPlatformCodes}
          activeCode={activeCode}
          onSelectCode={setActiveCode}
          platforms={platforms}
          addingCodes={addingCodes}
          isAdmin={isAdmin}
          ver={ver}
          showOverlay={reloading || submitting || awaitingReload}
          hasActiveInitial={initialPlatforms[activeCode] !== undefined}
          onPickAdd={handlePickAddPlatform}
          onChangeField={setPlatformField}
          onLookupNetwork={lookupNetwork}
          lookingUpNetwork={lookingUpNetwork}
          onLookupAllNetworks={lookupAllNetworks}
        />
      )}

      {/* 변경 사항 카드 */}
      <ChangeSummaryCard
        diffs={diffs}
        isAdmin={isAdmin}
        reason={reason}
        onChangeReason={setReason}
        submitting={submitting}
        onReset={reset}
        onSubmit={submit}
      />

      {/* 앱 정보 반영 과정 스텝퍼 — admin 즉시 반영 시 단계별 진행 표시 */}
      <ProcessStepper
        steps={processSteps}
        title="앱 정보 반영 중"
        onClose={() => setProcessSteps(null)}
      />
    </div>
  );
}
